import { environment } from "src/environments/environment";
import { Injectable } from "@angular/core";
import {
  BatchQuotaType,
  BatchRequestType,
  DeploymentScope,
  EnableType,
  IAppServiceQuotaUI,
  IBasicInfo,
  IBatchSku,
  IDetailsInfo,
  IDedicatedHostSku,
  IJustification,
  IParentRequest,
  IRequestDetails,
  IRequestRegion,
  IRequestVmSku,
  ISummaryInfo,
  ITeamListRecord,
  IUIRequest,
  IVmDiskQuotaInfo,
  SkuType,
  SqlMiType,
  SqlPurchaseModelType,
  allowedCommonRegionStatuses,
  allowedClouds,
} from "src/app/npr-request.model";
import { ApiService } from "src/app/services";
import { Region } from "src/app/generated-models";
import { HdiSku } from "src/app/generated-models/HdiSku";

const DEFAULT_SQL_MI_TOTAL_VCORES = 24;

@Injectable({
  providedIn: "root",
})
export class CreateRequestService {
  basicInfo: IBasicInfo;
  detailsInfo: IDetailsInfo;
  summaryInfo: ISummaryInfo = {
    utilizeDate: null,
    justification: [
      {
        Question: "",
        Answer: "",
        IsRequired: false,
      } as IJustification,
    ],
    subIds: "",
    newSubToCreate: null,
    subDisplayName: "",
    subTenantId: environment.tenantId,
    subOwnerObjId: "",
    subServiceTreeId: "",
    subServiceTree2ndLevelId: "",
    subPcCode: "",
    pamApprovalState: null,
    isSubmitBtnDisabled: () => true,
  };
  uiRequest: IUIRequest;
  csvFileName = "";
  isFlighting = false;
  cloneSourceRequest: IParentRequest | null = null;
  cloneLoadFailed = false;
  private cloneReadyCallback: (() => void) | null = null;
  teamList: ITeamListRecord[] = [];
  regionList: IRequestRegion[] = [];
  vmSkuList: IRequestVmSku[] = [];
  dedicatedHostSkuList: IDedicatedHostSku[] = [];
  batchSkuList: IBatchSku[] = [];
  hdiSkusInAllRegions: HdiSku[] = [];
  appServiceSkuList: IAppServiceQuotaUI[] = [];
  restrictedRegionsForCosmosDB: string[] = [];
  namespaces: string[] = [];
  armResourceTypes: string[] = [];
  vmDisks: IVmDiskQuotaInfo[] = [];
  groupQuotaVmSkuList: IRequestVmSku[] = [];
  private allRegion: IRequestRegion[] = [];

  constructor(private apiService: ApiService) {
    this.initDetailsInfo();
  }

  isCustomerRequest(): boolean {
    return this.basicInfo.customerRequest && this.basicInfo.isExternal;
  }

  initBasicInfo(): void {
    this.basicInfo = {
      isExternal: false,
      region: null,
      team: null,
      otherTeam: "",
      otherTeamOid: "",
      customer: "",
      email: "",
      subId: "",
      isRegionChanged: false,
      isNextBtnDisabled: () => true,
      subscriptions: "",
      customerRequest: false,
      subscriptionsByCSV: false,
    };
  }

  initDetailsInfo(): void {
    this.detailsInfo = {
      azEnablement: false,
      regionEnablement: false,
      regionEnablementARM: false,
      regionEnablementRDFE: false,
      sql: false,
      sqlPurchaseModel: null,
      sqlQuota: null,
      sqlDtu: false,
      sqlServerName: "",
      sqlDtuQuota: null,
      sqlServer: false,
      sqlServerQuota: null,
      sqlMI: false,
      sqlMiType: SqlMiType.Default,
      sqlMiGPvCores: null,
      sqlMiGPCount: null,
      sqlMiBCvCores: null,
      sqlMiBCCount: null,
      sqlMiTotalvCores: null,
      sqlMiSubNet: null,
      hdi: false,
      hdiQuota: null,
      hdiSku: null,
      dns: {
        IsSubsriptionQuota: false,
        IsZoneQuota: false
      },
      requireComputeArmVmQuota: false,
      requireComputeDedicatedHostQuota: false,
      vmQuota: [],
      dedicatedHostQuota: null,
      deploymentScope: DeploymentScope.Regional,
      requireComputeArmSharedQuota: false,
      computeArmSharedQuota: null,
      requireComputeArmLowPriorityQuota: false,
      computeArmLowPriorityQuota: null,
      requireComputeRdfeVmQuota: false,
      computeRdfeVmQuota: null,
      rdfeVmSku: null,
      additionalStorage: false,
      storageQuotaARM: null,
      storageQuotaRDFE: null,
      batchQuotaType: BatchQuotaType.Account,
      batchTotalAccounts: null,
      batchAccountName: null,
      batchComputeQuota: [],
      requireBatchAccounts: false,
      requireBatchComputeQuota: false,
      azEntities: [],
      cosmosDB: {
        IsAccess: false,
        IsSubscriptionAccounts: false,
        IsAccountQuota: false,
      },
      kusto: false,
      requireAppServiceQuota: false,
      appServiceQuota: this.detailsInfo?.appServiceQuota || [],
      serviceStatus: this.detailsInfo?.serviceStatus || {},
      rpFrontload: false,
      enableArmWithNoQuota: false,
      isValidateBtnDisabled: () => true,
      serviceTreeId: null,
      namespaces: null,
      requireArmResourceType: false,
      armResourceTypes: null,
      requireVmDisks: null,
      vmDisksSku: null,
      vmDisksQuota: null,
      zones: [],
      zonalCas: [],
      requireGroupQuota: false,
      groupQuotaName: null,
      managementGroupId: null,
      tenantId: null,
      groupQuotaVmSkus: [],
    };
  }

  getTeamList(): void {
    // Team List
    this.apiService?.getTeamList().subscribe((response) => {
      if (response) {
        const regionlessTeam = [];
        this.teamList = [];
        response.map((r) => {
          if (r.Ring) {
            this.teamList.push({
              label: `${r.Name} (${r.Oid})`,
              value: 0,
              ring: r.Ring,
              oid: r.Oid,
            } as ITeamListRecord);
          } else {
            regionlessTeam.push({
              label: `${r.Name} (${r.Oid})`,
              value: 0,
              ring: "No Ring",
              oid: r.Oid,
            } as ITeamListRecord);
          }
        });
        this.teamList = this.teamList
          .sort((a, b) => {
            return a.ring?.localeCompare(b.ring) || a.label?.localeCompare(b.label);
          })
          .concat(regionlessTeam.sort((a, b) => a.label?.localeCompare(b.label)));
        this.teamList.push({
          value: 0,
          label: "Other",
          ring: "Other",
          oid: "",
        } as ITeamListRecord);
        this.tryApplyCloneData();
      }
    });
  }

  getRegionList(): void {
    // Region List
    this.apiService?.getRegionList(false, true).subscribe((response: Region[]) => {
      if (!response) {
        console.warn("No region is obtained from api server.");
      } else {
        this.allRegion = response.filter(region => allowedCommonRegionStatuses.includes(region.Status) && allowedClouds.includes(region.CloudName)).map((r, i) => {
            return ApiService.createRequestRegionFromRegion(r, i + 1);
          });
        this.changeFlighting();
        this.tryApplyCloneData();
      }
    });
  }

  getSKUList(): void {
    this.vmSkuList = [];
    this.dedicatedHostSkuList = [];
    // VM Skus
    this.apiService?.getVmSkus().subscribe((response) => {
      if (!response) {
        console.warn("No vm sku returned from api");
      } else {
        response.map((r) => {
          if (r.PortalVmSku && r.CrpVmSku) {
            this.vmSkuList.push({
              label: r.PortalVmSku,
              value: r.CrpVmSku,
              checked: false,
              disabled: false,
              skuType: SkuType.Compute,
            } as IRequestVmSku);
            const dedicatedHostSku: IDedicatedHostSku = {
              label: r.PortalVmSku,
              value: r.CrpVmSku,
              disabled: false,
              skuType: SkuType.Compute,
            };
            this.dedicatedHostSkuList.push(dedicatedHostSku);
          }
          else {
            this.vmSkuList.push({
              label: r.CrpVmSku,
              value: r.CrpVmSku,
              checked: false,
              disabled: false,
              skuType: SkuType.Compute,
            } as IRequestVmSku);
            const dedicatedHostSku: IDedicatedHostSku = {
              label: r.CrpVmSku,
              value: r.CrpVmSku,
              disabled: false,
              skuType: SkuType.Compute,
            };
            this.dedicatedHostSkuList.push(dedicatedHostSku);
          }
        });

        // Remove duplicated elements
        this.vmSkuList = this.vmSkuList.filter((thing, index, self) => self.findIndex(t => t.label === thing.label && t.value === thing.value) === index);
        this.vmSkuList.sort((a, b) => a.label?.localeCompare(b.label));
        this.dedicatedHostSkuList = this.dedicatedHostSkuList.filter((thing, index, self) => self.findIndex(t => t.label === thing.label && t.value === thing.value) === index);
        this.dedicatedHostSkuList.sort((a, b) => a.label?.localeCompare(b.label));
      }
    });

    // Hdi skus in all regions
    this.apiService?.getHdiSkus().subscribe((response) => {
      if (!response) {
        console.warn("No Hdi sku is obtained from api server.");
      } else {
        this.hdiSkusInAllRegions = response;
      }
    });

    // Batch skus in all regions
    this.apiService?.getBatchSkus().subscribe((response) => {
      this.batchSkuList = [];
      if (!response) {
        console.warn("No Batch sku is obtained from api server.");
      } else {
        response.map((r) => {
          if (r.PortalVmSku && r.CrpVmSku) {
            this.batchSkuList.push({
              region: r.RegionName,
              requestType: BatchRequestType.Dedicated,
              label: r.PortalVmSku,
              disabled: false,
              sku: r.CrpVmSku,
            } as IBatchSku);
          }
        });

        // Batch Low priority SKU
        this.batchSkuList.push({
          region: "all",
          requestType: BatchRequestType.LowPriority,
          label: "Low Priority Quota",
          disabled: false,
          sku: "Low Priority Quota",
        } as IBatchSku);

        // Batch account pool quota
        this.batchSkuList.push({
          region: "all",
          requestType: BatchRequestType.PoolQuota,
          label: "Pools per Batch Account",
          disabled: false,
          sku: "Pools per Batch Account",
        } as IBatchSku);

        // Batch account job quota
        this.batchSkuList.push({
          region: "all",
          requestType: BatchRequestType.JobQuota,
          label: "Active Jobs Schedule Per Batch Account",
          disabled: false,
          sku: "Active Jobs Schedule Per Batch Account",
        } as IBatchSku);
      }
    });

    this.apiService?.getAppServiceSkus().subscribe((response) => {
      if (!response) {
        console.warn("No app service sku is obtained from api server.");
      } else {
        this.appServiceSkuList = [];
        response.forEach((sku) => {
          this.appServiceSkuList.push({
            offering: sku,
          } as IAppServiceQuotaUI);
        });
      }
    });
  }

  getNameSpaceList(): void {
    this.namespaces = [];
    this.apiService?.getNamespaceList().subscribe((response) => {
      if (!response) {
        console.warn("No namespace is obtained from api server.");
      } else {
        this.namespaces = response;
      }
    });
  }

  GetCosmosDBAzRestrictedRegions(): void {
    this.apiService?.getRestrictedRegionListForCosmosDB().subscribe((regions) => {
      if (regions && regions.length > 0) {
        this.restrictedRegionsForCosmosDB = regions;
      }
    });
  }

  changeFlighting(): void {
    this.regionList = this.allRegion.filter((r) => r.isEnabled || (this.isFlighting && r.isFlighting));
    if (!this.regionList.includes(this.basicInfo.region)) {
      this.basicInfo.region = null;
    }
  }

  loadCloneSource(requestId: string, onReady: () => void, onError?: (err: unknown) => void): void {
    this.cloneLoadFailed = false;
    this.cloneReadyCallback = onReady;
    this.apiService.getParentRequest(requestId).subscribe({
      next: (request) => {
        this.cloneSourceRequest = request;
        this.basicInfo.subId = request.SubscriptionId;
        this.basicInfo.isExternal = request.IsExternal;
        if (request.IsExternal) {
          this.basicInfo.customer = request.Requestor;
        }
        this.initDetailsInfo();
        this.populateDetailsInfoFromRequest(request.Details);
        this.tryApplyCloneData();
      },
      error: (err) => {
        this.cloneSourceRequest = null;
        this.cloneLoadFailed = true;
        this.cloneReadyCallback = null;
        onError?.(err);
      },
    });
  }

  private tryApplyCloneData(): void {
    if (!this.cloneSourceRequest) {
      return;
    }
    if (this.regionList.length === 0 || this.teamList.length === 0) {
      return;
    }

    const req = this.cloneSourceRequest;
    this.cloneSourceRequest = null;

    if (!req.IsExternal && req.TeamOid) {
      const team = this.teamList.find(t => t.oid === req.TeamOid);
      if (team) {
        this.basicInfo.team = team;
      }
    }

    const region = this.regionList.find(r => r.label === req.Region);
    if (region) {
      this.basicInfo.region = region;
    }

    if (this.cloneReadyCallback) {
      this.cloneReadyCallback();
      this.cloneReadyCallback = null;
    }
  }

  private populateDetailsInfoFromRequest(details: IRequestDetails): void {
    if (!details) {
      return;
    }

    const d = this.detailsInfo;

    d.enableArmWithNoQuota = details.EnableArmWithNoQuota ?? false;

    if (d.enableArmWithNoQuota) {
      return;
    }

    if (details.Enable) {
      d.azEnablement = details.Enable.includes(EnableType.AZ);
      const hasArm = details.Enable.includes(EnableType.ARM);
      const hasRdfe = details.Enable.includes(EnableType.RDFE);
      d.regionEnablement = hasArm || hasRdfe;
      d.regionEnablementARM = hasArm;
      d.regionEnablementRDFE = hasRdfe;
      d.sql = details.Enable.includes(EnableType.SQL);
      d.kusto = details.Enable.includes(EnableType.Kusto);
      if (details.Enable.includes(EnableType.CosmosDB)) {
        d.cosmosDB.IsAccess = true;
      }
      if (details.Enable.includes(EnableType.CosmosDBAz)) {
        d.cosmosDB.IsAzEnablement = true;
      }
      d.rpFrontload = details.Enable.includes(EnableType.RPFrontload);
    }

    if (d.azEnablement && details.Info?.Zones) {
      d.zones = details.Info.Zones.split(",").map(z => z.trim()).filter(z => z.length > 0);
    }

    if (details.SqlDtuQuota && Object.keys(details.SqlDtuQuota).length > 0) {
      const key = Object.keys(details.SqlDtuQuota)[0];
      d.sqlDtu = true;
      d.sqlServerName = key;
      d.sqlDtuQuota = details.SqlDtuQuota[key];
    }

    if (details.SqlQuota?.SQLServerCount) {
      d.sqlServer = true;
      d.sqlServerQuota = details.SqlQuota.SQLServerCount;
    }

    if (details.SqlQuota?.SQLMI) {
      d.sqlMI = true;
      if (details.SqlQuota.SQLMI === DEFAULT_SQL_MI_TOTAL_VCORES && (!details.SqlQuota.SqlMiSubNet || details.SqlQuota.SqlMiSubNet === 1)) {
        d.sqlMiType = SqlMiType.Default;
      } else {
        d.sqlMiType = SqlMiType.TotalvCores;
        d.sqlMiTotalvCores = details.SqlQuota.SQLMI;
        d.sqlMiSubNet = details.SqlQuota.SqlMiSubNet ?? 1;
      }
    }

    if (d.sql) {
      if (details.SqlQuota?.SQL) {
        d.sqlQuota = details.SqlQuota.SQL;
        d.sqlPurchaseModel = SqlPurchaseModelType.VCores;
      } else if (details.SqlQuota?.SQL_ByDtu) {
        d.sqlQuota = details.SqlQuota.SQL_ByDtu;
        d.sqlPurchaseModel = SqlPurchaseModelType.DTUs;
      }
    }

    if (details.HdiQuota) {
      d.hdi = true;
      d.hdiQuota = details.HdiQuota;
      if (details.Info?.HdiSku) {
        d.hdiSku = { value: details.Info.HdiSku, label: details.Info.HdiSku };
      }
    }

    if (details.BatchQuota?.TotalAccounts > 0) {
      d.requireBatchAccounts = true;
      d.batchTotalAccounts = details.BatchQuota.TotalAccounts;
    }

    if (details.BatchQuota?.PerAccountQuotas) {
      const accountNames = Object.keys(details.BatchQuota.PerAccountQuotas);
      if (accountNames.length > 0) {
        d.requireBatchComputeQuota = true;
        d.batchAccountName = accountNames[0];
        const perAccount = details.BatchQuota.PerAccountQuotas[accountNames[0]];
        d.batchComputeQuota = [];
        if (perAccount.LowPriorityCoreQuota > 0) {
          d.batchComputeQuota.push({
            requestType: BatchRequestType.LowPriority,
            sku: "Low Priority Quota",
            label: "Low Priority Quota",
            quota: perAccount.LowPriorityCoreQuota,
            disabled: false,
            region: "all",
          } as IBatchSku);
        }
        if (perAccount.PoolQuota > 0) {
          d.batchComputeQuota.push({
            requestType: BatchRequestType.PoolQuota,
            sku: "Pools per Batch Account",
            label: "Pools per Batch Account",
            quota: perAccount.PoolQuota,
            disabled: false,
            region: "all",
          } as IBatchSku);
        }
        if (perAccount.JobQuota > 0) {
          d.batchComputeQuota.push({
            requestType: BatchRequestType.JobQuota,
            sku: "Active Jobs Schedule Per Batch Account",
            label: "Active Jobs Schedule Per Batch Account",
            quota: perAccount.JobQuota,
            disabled: false,
            region: "all",
          } as IBatchSku);
        }
        if (perAccount.VmQuota) {
          Object.entries(perAccount.VmQuota).forEach(([sku, quota]) => {
            d.batchComputeQuota.push({
              requestType: BatchRequestType.Dedicated,
              sku,
              label: sku,
              quota,
              disabled: false,
              region: "all",
            } as IBatchSku);
          });
        }
      }
    }

    if (details.StorageAccountQuota?.ARM || details.StorageAccountQuota?.RDFE) {
      d.additionalStorage = true;
      d.storageQuotaARM = details.StorageAccountQuota.ARM ?? null;
      d.storageQuotaRDFE = details.StorageAccountQuota.RDFE ?? null;
    }

    if (details.DeploymentScope) {
      d.deploymentScope = details.DeploymentScope;
    }

    // API response uses "VmQuota" (PascalCase) while the interface defines "VMQuota"
    const vmQuota: { [key: string]: number } = details.VMQuota ?? details.VmQuota;
    if (vmQuota && Object.keys(vmQuota).length > 0) {
      d.requireComputeArmVmQuota = true;
      Object.entries(vmQuota).forEach(([sku, quota]) => {
        if (sku === "*") {
          d.requireComputeArmSharedQuota = true;
          d.computeArmSharedQuota = quota;
        } else if (sku === "Low Priority") {
          d.requireComputeArmLowPriorityQuota = true;
          d.computeArmLowPriorityQuota = quota;
        } else {
          d.vmQuota.push({
            value: sku,
            label: sku,
            checked: true,
            quota,
            disabled: false,
            skuType: SkuType.Compute,
            zones: details.VmZones?.[sku] ?? [],
          } as IRequestVmSku);
        }
      });
    }

    if (details.VmSkus?.length > 0) {
      d.requireComputeArmVmQuota = true;
      details.VmSkus.forEach(sku => {
        if (!d.vmQuota.some(vm => vm.value === sku)) {
          d.vmQuota.push({
            value: sku,
            label: sku,
            checked: false,
            quota: null,
            disabled: false,
            skuType: SkuType.Compute,
            zones: details.VmZones?.[sku] ?? [],
          } as IRequestVmSku);
        }
      });
    }

    if (details.dedicatedHostQuota?.Sku) {
      d.requireComputeDedicatedHostQuota = true;
      d.dedicatedHostQuota = {
        value: details.dedicatedHostQuota.Sku,
        label: details.dedicatedHostQuota.Sku,
        quota: details.dedicatedHostQuota.Quota,
        disabled: false,
        skuType: SkuType.Compute,
      } as IDedicatedHostSku;
    }

    if (details.RdfeVmQuota) {
      d.requireComputeRdfeVmQuota = true;
      d.computeRdfeVmQuota = details.RdfeVmQuota;
      if (details.Info?.RdfeVmSku) {
        d.rdfeVmSku = {
          value: details.Info.RdfeVmSku,
          label: details.Info.RdfeVmSku,
          checked: false,
          quota: null,
          disabled: false,
          skuType: SkuType.Compute,
          zones: [],
        } as IRequestVmSku;
      }
    }

    if (details.CosmosDbQuota?.SubscriptionAccounts) {
      d.cosmosDB.IsSubscriptionAccounts = true;
      d.cosmosDB.SubscriptionAccounts = details.CosmosDbQuota.SubscriptionAccounts;
    }

    if (details.CosmosDbQuota?.PerAccountQuotas) {
      const accountNames = Object.keys(details.CosmosDbQuota.PerAccountQuotas);
      if (accountNames.length > 0) {
        d.cosmosDB.IsAccountQuota = true;
        d.cosmosDB.AccountName = accountNames[0];
      }
    }

    if (details.AppServiceQuotas) {
      d.requireAppServiceQuota = true;
      d.appServiceQuota = Object.entries(details.AppServiceQuotas).map(([offering, q]) => ({
        offering,
        windowsVmQuota: q.WindowsVmQuota,
        linuxVmQuota: q.LinuxVmQuota,
      } as IAppServiceQuotaUI));
    }

    if (d.rpFrontload && details.RPFrontload?.length > 0) {
      d.namespaces = details.RPFrontload[0].Namespace;
      d.serviceTreeId = details.RPFrontload[0].ServiceTreeId;
    }

    if (details.ArmResourceTypes?.length > 0) {
      d.requireArmResourceType = true;
      d.armResourceTypes = [...details.ArmResourceTypes];
    }

    if (details.VmDisksQuota && Object.keys(details.VmDisksQuota).length > 0) {
      const sku = Object.keys(details.VmDisksQuota)[0];
      d.requireVmDisks = true;
      d.vmDisksSku = { value: sku, label: sku, maxQuotaLimit: 0 } as IVmDiskQuotaInfo;
      d.vmDisksQuota = details.VmDisksQuota[sku];
    }

    if (details.GroupQuotaDetails?.GroupQuotaName) {
      d.requireGroupQuota = true;
      d.groupQuotaName = details.GroupQuotaDetails.GroupQuotaName;
      d.managementGroupId = details.GroupQuotaDetails.ManagementGroupId;
      d.tenantId = details.GroupQuotaDetails.TenantId;
      if (details.DeploymentScope) {
        d.deploymentScope = details.DeploymentScope;
      }
      if (details.GroupQuotaDetails.VmQuota) {
        d.groupQuotaVmSkus = Object.entries(details.GroupQuotaDetails.VmQuota).map(([sku, quota]) => ({
          value: sku,
          label: sku,
          checked: true,
          quota,
          disabled: false,
          skuType: SkuType.Compute,
          zones: [],
        } as IRequestVmSku));
      }
    }

    if (details.DnsEntryQuota) {
      if (details.DnsEntryQuota.quota > 0) {
        d.dns.IsSubsriptionQuota = true;
        d.dns.maxZoneQuota = details.DnsEntryQuota.quota;
      }
      if (details.DnsEntryQuota.recordSetQuota > 0) {
        d.dns.IsZoneQuota = true;
        d.dns.maxRecordSetQuota = details.DnsEntryQuota.recordSetQuota;
      }
      if (details.DnsEntryQuota.perZoneQuotas) {
        const zoneNames = Object.keys(details.DnsEntryQuota.perZoneQuotas);
        if (zoneNames.length > 0) {
          d.dns.zoneName = zoneNames[0];
          d.dns.resourceGroup = details.DnsEntryQuota.perZoneQuotas[zoneNames[0]].resourceGroup;
          d.dns.zoneRecordSetQuota = details.DnsEntryQuota.perZoneQuotas[zoneNames[0]].zoneRecordSetQuota;
        }
      }
    }
  }
}
